import { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";

/**
 * PWA install prompt banner — Iter152, extended Iter160e for iOS.
 *
 * Chrome / Edge / Samsung Internet → fire `beforeinstallprompt`, so we
 * show a one-click Install button.
 *
 * Safari on iOS / iPadOS → does NOT fire that event. Apple requires the
 * user to manually tap Share → "Add to Home Screen". We detect iOS
 * Safari and show an instructional banner with the Share icon + the
 * two-step recipe so reps know how to install from their phone.
 *
 * Both paths suppress for 14 days after a dismissal.
 */
const SUPPRESS_KEY = "warmap.pwa_install_dismissed_at";
const SUPPRESS_MS = 14 * 24 * 60 * 60 * 1000;
const IOS_SEEN_KEY = "warmap.ios_install_hint_seen_at";

function isIosSafari() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  // iOS / iPadOS detection. iPadOS 13+ reports as Mac but advertises touch.
  const isIos = /iPad|iPhone|iPod/.test(ua) ||
    (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
  // Inside an in-app webview (Instagram, FB, etc.) Safari's "Add to Home
  // Screen" isn't available, so don't lie to the user.
  const isInWebView = /(FBAN|FBAV|Instagram|Line\/|Twitter|WebView)/.test(ua);
  return isIos && !isInWebView;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function PWAInstallPrompt() {
  const [event, setEvent] = useState(null);
  const [show, setShow] = useState(false);
  const [iosMode, setIosMode] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;

    const dismissedAt = Number(localStorage.getItem(SUPPRESS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < SUPPRESS_MS) return;

    const handler = (e) => {
      e.preventDefault();
      setEvent(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS path — no platform event ever comes, so we surface our own
    // banner the first time a logged-in iOS user lands on a page.
    // Delayed 3s so it doesn't fight the login flow.
    if (isIosSafari()) {
      const t = window.setTimeout(() => {
        if (!isStandalone()) {
          setIosMode(true);
          setShow(true);
        }
      }, 3000);
      return () => {
        window.removeEventListener("beforeinstallprompt", handler);
        window.clearTimeout(t);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  if (!show) return null;

  const handleInstall = async () => {
    if (iosMode) {
      // No programmatic install on iOS — record they've seen the hint
      // and dismiss. We keep the SUPPRESS_KEY long enough to avoid nagging.
      localStorage.setItem(IOS_SEEN_KEY, String(Date.now()));
      localStorage.setItem(SUPPRESS_KEY, String(Date.now()));
      setShow(false);
      return;
    }
    if (!event) return;
    try {
      event.prompt();
      const choice = await event.userChoice;
      if (choice.outcome === "accepted") {
        setShow(false);
      } else {
        localStorage.setItem(SUPPRESS_KEY, String(Date.now()));
        setShow(false);
      }
    } catch (e) {
      setShow(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(SUPPRESS_KEY, String(Date.now()));
    setShow(false);
  };

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 max-w-md w-[calc(100%-2rem)]"
      data-testid="pwa-install-prompt"
    >
      <div className="bg-gradient-to-br from-[#064e3b] to-[#0c8159] border border-emerald-500/30 rounded-xl shadow-2xl shadow-emerald-500/10 p-4 flex items-start gap-3">
        <div className="bg-white/10 rounded-lg p-2 shrink-0">
          <Download className="w-5 h-5 text-emerald-200" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-semibold text-sm">
            {iosMode ? "Install WarMap on your iPhone" : "Install WarMap"}
          </h4>
          {iosMode ? (
            <div className="mt-1 space-y-1.5">
              <p className="text-xs text-emerald-100/85 leading-relaxed">
                Get a one-tap app icon on your Home Screen plus push alerts.
              </p>
              <div className="text-xs text-emerald-100/90 space-y-1 mt-2">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/15 text-[10px] font-semibold">1</span>
                  <span>Tap the</span>
                  <Share className="w-4 h-4 text-emerald-100 inline" strokeWidth={2.5} />
                  <span>Share button in Safari</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/15 text-[10px] font-semibold">2</span>
                  <span>Tap "Add to Home Screen"</span>
                  <Plus className="w-4 h-4 text-emerald-100 inline" strokeWidth={2.5} />
                </div>
              </div>
            </div>
          ) : (
            <p className="text-xs text-emerald-100/80 mt-0.5">
              Get OS-level push notifications and a faster app-like experience.
            </p>
          )}
          <div className="mt-3 flex gap-2">
            <button
              onClick={handleInstall}
              data-testid="pwa-install-confirm-btn"
              className="px-3 py-1.5 bg-white text-emerald-900 text-xs font-semibold rounded-lg hover:bg-emerald-50 transition-colors"
            >
              {iosMode ? "Got it" : "Install"}
            </button>
            <button
              onClick={handleDismiss}
              data-testid="pwa-install-dismiss-btn"
              className="px-3 py-1.5 text-emerald-100/80 text-xs hover:text-white transition-colors"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          aria-label="Close install prompt"
          className="text-emerald-100/60 hover:text-white transition-colors shrink-0"
          data-testid="pwa-install-close-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
